import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useWatchlist } from '../../../hooks/useWatchlist';
import type { WatchlistItem } from '../../../hooks/useWatchlist';

type FilterType = 'all' | 'dramas' | 'ai-projects';
type StatusFilter = 'all' | 'watching' | 'completed' | 'plan-to-watch';

const MyWatchlist = () => {
  const { items, removeFromWatchlist, updateStatus } = useWatchlist();
  const [typeFilter, setTypeFilter] = useState<FilterType>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null);

  const filteredItems = items.filter((item) => {
    const typeMatch =
      typeFilter === 'all' ||
      (typeFilter === 'dramas' && item.type === 'drama') ||
      (typeFilter === 'ai-projects' && item.type === 'ai-project');
    const statusMatch = statusFilter === 'all' || item.status === statusFilter;
    return typeMatch && statusMatch;
  });

  const stats = {
    total: items.length,
    watching: items.filter((i) => i.status === 'watching').length,
    completed: items.filter((i) => i.status === 'completed').length,
    planToWatch: items.filter((i) => i.status === 'plan-to-watch').length,
  };

  const handleRemove = (id: string) => {
    removeFromWatchlist(id);
    setShowRemoveConfirm(null);
  };

  const handleStatusChange = (id: string, newStatus: WatchlistItem['status']) => {
    updateStatus(id, newStatus);
  };

  const getStatusBadge = (status: WatchlistItem['status']) => {
    switch (status) {
      case 'watching':
        return (
          <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
            Watching
          </span>
        );
      case 'completed':
        return (
          <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full flex items-center gap-1">
            <i className="ri-check-double-line text-xs"></i>
            Completed
          </span>
        );
      case 'plan-to-watch':
        return (
          <span className="px-2.5 py-1 bg-rose-100 text-rose-700 text-xs font-semibold rounded-full flex items-center gap-1">
            <i className="ri-bookmark-line text-xs"></i>
            Plan to Watch
          </span>
        );
    }
  };

  const getTypeBadge = (type: WatchlistItem['type']) => {
    if (type === 'ai-project') {
      return (
        <span className="px-2 py-0.5 bg-gradient-to-r from-rose-500 to-orange-500 text-white text-[10px] font-bold rounded uppercase tracking-wider">
          AI Project
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 bg-gray-800 text-white text-[10px] font-bold rounded uppercase tracking-wider">
        Drama
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-3">
        <button
          onClick={() => setStatusFilter('all')}
          className={`p-3 rounded-xl text-center transition-all cursor-pointer ${
            statusFilter === 'all'
              ? 'bg-rose-50 border-2 border-rose-400'
              : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
          }`}
        >
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-xs text-gray-500">All Saved</p>
        </button>
        <button
          onClick={() => setStatusFilter('watching')}
          className={`p-3 rounded-xl text-center transition-all cursor-pointer ${
            statusFilter === 'watching'
              ? 'bg-emerald-50 border-2 border-emerald-400'
              : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
          }`}
        >
          <p className="text-2xl font-bold text-emerald-600">{stats.watching}</p>
          <p className="text-xs text-gray-500">Watching</p>
        </button>
        <button
          onClick={() => setStatusFilter('completed')}
          className={`p-3 rounded-xl text-center transition-all cursor-pointer ${
            statusFilter === 'completed'
              ? 'bg-amber-50 border-2 border-amber-400'
              : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
          }`}
        >
          <p className="text-2xl font-bold text-amber-600">{stats.completed}</p>
          <p className="text-xs text-gray-500">Completed</p>
        </button>
        <button
          onClick={() => setStatusFilter('plan-to-watch')}
          className={`p-3 rounded-xl text-center transition-all cursor-pointer ${
            statusFilter === 'plan-to-watch'
              ? 'bg-rose-50 border-2 border-rose-400'
              : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
          }`}
        >
          <p className="text-2xl font-bold text-rose-600">{stats.planToWatch}</p>
          <p className="text-xs text-gray-500">Plan to Watch</p>
        </button>
      </div>

      {/* Type Filter */}
      <div className="flex items-center gap-2">
        {[
          { id: 'all' as FilterType, label: 'All', icon: 'ri-apps-line' },
          { id: 'dramas' as FilterType, label: 'Dramas', icon: 'ri-film-line' },
          { id: 'ai-projects' as FilterType, label: 'AI Projects', icon: 'ri-robot-2-line' },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setTypeFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              typeFilter === f.id
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <i className={`${f.icon} mr-1.5`}></i>
            {f.label}
          </button>
        ))}
        <div className="flex-1"></div>
        <span className="text-sm text-gray-500">
          {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Watchlist Items */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-xl">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-bookmark-line text-4xl text-gray-300"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {statusFilter === 'all' && typeFilter === 'all'
              ? 'Your watchlist is empty'
              : 'No items match your filters'}
          </h3>
          <p className="text-gray-500 text-sm mb-5 max-w-sm mx-auto">
            {statusFilter === 'all' && typeFilter === 'all'
              ? 'Start adding dramas and AI projects to your watchlist to keep track of what you want to watch.'
              : 'Try adjusting your filters to see more items.'}
          </p>
          <Link
            to="/dramas"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-compass-3-line"></i>
            Browse Dramas
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-md transition-all group"
            >
              <div className="flex">
                {/* Poster */}
                <Link to={item.url} className="flex-shrink-0 relative">
                  <div className="w-28 h-40 overflow-hidden">
                    <img
                      src={item.poster}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="absolute top-2 left-2">
                    {getTypeBadge(item.type)}
                  </div>
                </Link>

                {/* Info */}
                <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
                  <div>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <Link
                          to={item.url}
                          className="font-semibold text-gray-900 hover:text-rose-600 transition-colors line-clamp-1 text-base"
                        >
                          {item.title}
                        </Link>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                          <span>{item.year}</span>
                          <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                          <span>{item.genre}</span>
                          <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                          <span>{item.duration}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {getStatusBadge(item.status)}
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-1.5 mb-3">
                      <i className="ri-star-fill text-yellow-400 text-sm"></i>
                      <span className="text-sm font-semibold text-gray-900">{item.rating}</span>
                      {item.episodes && (
                        <>
                          <span className="w-1 h-1 bg-gray-300 rounded-full mx-1"></span>
                          <span className="text-sm text-gray-500">{item.episodes} episodes</span>
                        </>
                      )}
                    </div>

                    {/* Progress Bar (for dramas with episodes) */}
                    {item.type === 'drama' && item.totalEpisodes && item.status !== 'plan-to-watch' && (
                      <div className="mb-2">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                          <span>Progress</span>
                          <span className="font-medium text-gray-700">
                            {item.progress || 0}/{item.totalEpisodes} episodes
                          </span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              item.status === 'completed'
                                ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                                : 'bg-gradient-to-r from-emerald-400 to-emerald-500'
                            }`}
                            style={{
                              width: `${((item.progress || 0) / item.totalEpisodes) * 100}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">
                      Added {new Date(item.addedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <div className="flex items-center gap-1">
                      {/* Status Change Dropdown */}
                      <div className="relative group/dropdown">
                        <button className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer" title="Change status">
                          <i className="ri-arrow-left-right-line text-sm"></i>
                        </button>
                        <div className="absolute right-0 bottom-full mb-1 w-44 bg-white border border-gray-200 rounded-lg shadow-xl opacity-0 invisible group-hover/dropdown:opacity-100 group-hover/dropdown:visible transition-all z-20">
                          {(['watching', 'completed', 'plan-to-watch'] as const).map((s) => (
                            <button
                              key={s}
                              onClick={() => handleStatusChange(item.id, s)}
                              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors cursor-pointer first:rounded-t-lg last:rounded-b-lg flex items-center gap-2 ${
                                item.status === s ? 'text-rose-600 font-medium bg-rose-50' : 'text-gray-700'
                              }`}
                            >
                              <i
                                className={`${
                                  s === 'watching'
                                    ? 'ri-play-circle-line'
                                    : s === 'completed'
                                    ? 'ri-check-double-line'
                                    : 'ri-bookmark-line'
                                } text-base`}
                              ></i>
                              {s === 'watching' ? 'Watching' : s === 'completed' ? 'Completed' : 'Plan to Watch'}
                              {item.status === s && <i className="ri-check-line ml-auto"></i>}
                            </button>
                          ))}
                        </div>
                      </div>

                      <Link
                        to={item.url}
                        className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                        title="View details"
                      >
                        <i className="ri-arrow-right-line text-sm"></i>
                      </Link>

                      {/* Remove */}
                      {showRemoveConfirm === item.id ? (
                        <div className="flex items-center gap-1 ml-1">
                          <button
                            onClick={() => handleRemove(item.id)}
                            className="px-2 py-1 bg-red-500 text-white text-xs rounded font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            Remove
                          </button>
                          <button
                            onClick={() => setShowRemoveConfirm(null)}
                            className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded font-medium hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowRemoveConfirm(item.id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                          title="Remove from watchlist"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Browse More */}
      {items.length > 0 && (
        <div className="text-center pt-4">
          <Link
            to="/dramas"
            className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium cursor-pointer"
          >
            <i className="ri-compass-3-line"></i>
            Discover More Dramas
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      )}
    </div>
  );
};

export default MyWatchlist;
